import { useCallback, useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useGuestRequestUpgradeStore } from '@confluence/external-collab-ui/entry-points/useGuestRequestUpgradeStore.ts';
import { useGuestRequestToUpgradeStatus } from '@confluence/external-collab-ui/entry-points/useGuestRequestToUpgradeStatus.ts';

export const ANALYTICS_SOURCE = 'noSpaceAccess';

const UI_EVENT = 'sendUIEvent';
const SCREEN_EVENT = 'sendScreenEvent';
const TRACK_EVENT = 'sendTrackEvent';
const CLICKED = 'clicked';
const BUTTON = 'button';

export const useRestrictedSpaceForGuest = (): {
	fireOnRequestSubmittedAnalytics: (hasError: boolean) => void;
	fireRequestAccessClickedAnalytics: () => void;
	fireLearnMoreClickedAnalytics: () => void;
	fireViewScreenAnalytics: (canRequestUpgrade: boolean) => void;
	showDialog: boolean;
	toggleDialog: () => void;
	isRequestAccessPending: boolean;
	loading: boolean;
	error: Error | undefined;
	canRequestUpgrade: boolean;
} => {
	const [showDialog, setShowDialog] = useState(false);
	const [{ requestAccessState }, { setRequestAccessState }] = useGuestRequestUpgradeStore();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { upgradeStatus, upgradeStatusReason, loading, error } = useGuestRequestToUpgradeStatus();

	useEffect(() => {
		setRequestAccessState(
			upgradeStatusReason === 'PENDING_REQUEST_EXISTS' ? 'submitted' : undefined,
		);
	}, [upgradeStatusReason, setRequestAccessState]);

	const toggleDialog = (): void => setShowDialog((prevState) => !prevState);

	const fireLearnMoreClickedAnalytics = (): void =>
		createAnalyticsEvent({
			type: UI_EVENT,
			data: {
				action: CLICKED,
				actionSubject: BUTTON,
				actionSubjectId: 'learnMoreLink',
				source: ANALYTICS_SOURCE,
			},
		}).fire();

	const fireRequestAccessClickedAnalytics = (): void =>
		createAnalyticsEvent({
			type: UI_EVENT,
			data: {
				action: CLICKED,
				actionSubject: BUTTON,
				actionSubjectId: 'requestAccess',
				source: ANALYTICS_SOURCE,
			},
		}).fire();

	const fireViewScreenAnalytics = useCallback(
		(canRequestUpgrade: boolean): void =>
			createAnalyticsEvent({
				type: SCREEN_EVENT,
				data: {
					actionSubjectId: canRequestUpgrade
						? 'guestEligibleToUpgrade'
						: 'guestIneligibleToUpgrade',
					name: ANALYTICS_SOURCE,
				},
			}).fire(),
		[createAnalyticsEvent],
	);

	const fireOnRequestSubmittedAnalytics = (hasError: boolean): void =>
		createAnalyticsEvent({
			type: TRACK_EVENT,
			data: {
				action: hasError ? 'failed' : 'requested',
				actionSubject: 'productAccess',
				source: ANALYTICS_SOURCE,
			},
		}).fire();

	return {
		fireOnRequestSubmittedAnalytics,
		fireRequestAccessClickedAnalytics,
		fireLearnMoreClickedAnalytics,
		fireViewScreenAnalytics,
		showDialog,
		toggleDialog,
		isRequestAccessPending: !!requestAccessState,
		loading,
		error,
		canRequestUpgrade: upgradeStatus == 'CAN_REQUEST',
	};
};
